import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'src/components/ui/Modal';
import Button from 'src/components/ui/Button';
import Input from 'src/components/ui/Input';
import Textarea from 'src/components/ui/Textarea';
import { Specialist, SpecialistCreator, ResourcePermissionRole, KnowledgeBase, BLANK_SPECIALIST_DRAFT } from '../types';
import {
  PlusIcon,
  TrashIcon,
  PencilSimpleIcon,
  CheckIcon,
  ArrowLeftIcon,
  QuestionIcon,
  ChatCircleIcon,
  ShareIcon,
  InfoIcon
} from '@phosphor-icons/react';
import { SearchInputField } from 'src/components/ui/SearchInput';
import { useSpecialist } from 'src/contexts/SpecialistContext';
import { usePostHog } from 'src/lib/posthog';
import { ConfirmModal } from 'src/components/ui/ConfirmModal';
import SpecialistShareModal from './SpecialistShareModal';
import SpecialistPill from './SpecialistPill';
import useAuth from 'src/hooks/useAuth';
import { useAggregations } from 'src/features/database/hooks/useAggregations';
import { useGroups } from 'src/features/groups/hooks';
import SourcesPanel, { groupSourceKey } from './SourcesPanel';
import { ResourceLink } from './ResourceLink';
import SegmentedControl from 'src/components/ui/SegmentedControl';
import DocumentPickerContent, {
  DocumentPickerFilters,
  EMPTY_DOCUMENT_PICKER_FILTERS
} from 'src/components/DocumentPickerContent';
import { useDocumentPicker, usePickerTitles } from 'src/components/useDocumentPicker';
import { useCategoryDefaults } from '../hooks/useCategoryDefaults';

// Predefined pastel colors for specialists
const PASTEL_COLORS = [
  '#ffb3ba', // Pastel Red
  '#ffdfba', // Pastel Orange
  '#ffffba', // Pastel Yellow
  '#baffc9', // Pastel Green
  '#bae1ff', // Pastel Blue
  '#c9b3ff', // Pastel Purple
  '#ffb3e6', // Pastel Pink
  '#e0bbe4', // Pastel Lavender
  '#ffdfd3', // Pastel Coral
  '#d4f1f4' // Pastel Cyan
];

// Internal component for color selection
interface ColorPickerProps {
  selectedColor?: string;
  onColorSelect: (color: string) => void;
  disabled?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ selectedColor, onColorSelect, disabled = false }) => {
  return (
    <div className="gap-1 flex flex-wrap">
      {PASTEL_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => !disabled && onColorSelect(color)}
          className="w-8 h-8 rounded-lg border-2 border-border-default hover:border-border-active disabled:cursor-not-allowed"
          style={{
            backgroundColor: color
          }}
          disabled={disabled}
          aria-label={`Select color ${color}`}
        >
          {selectedColor === color && <CheckIcon size={20} weight="light" className="m-auto text-content-subtle" />}
        </button>
      ))}
    </div>
  );
};

// Specialist card component for grid display
interface SpecialistCardProps {
  specialist: Specialist;
  onClick: () => void;
  getCreatorLabel: (creator?: SpecialistCreator | null) => string;
}

const SpecialistCard: React.FC<SpecialistCardProps> = ({ specialist, onClick, getCreatorLabel }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-start text-left px-3 py-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-gray-50 transition-colors"
  >
    <div className="mb-2 w-full">
      <SpecialistPill specialist={specialist} className="max-w-full" />
    </div>
    <p className="text-xs text-gray-500 line-clamp-3 flex-1 w-full">
      {specialist.prompt_parts.instruction || specialist.prompt_parts.response_style || 'No description'}
    </p>
    <div className="flex items-center gap-2 w-full mt-auto pt-2">
      {specialist.shared_by && (
        <span className="text-[10px] text-gray-400 truncate">Shared by {getCreatorLabel(specialist.shared_by)}</span>
      )}
      {!specialist.shared_by && !specialist.is_owner && (
        <span className="text-[10px] text-gray-400 truncate">by {getCreatorLabel(specialist.creator)}</span>
      )}
      {specialist.is_owner && (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-800">Yours</span>
      )}
    </div>
  </button>
);

// ============================================================
// KnowledgeBasePanels
// ============================================================

interface KnowledgeBasePanelsProps {
  knowledgeBase: KnowledgeBase;
  onChange: (kb: KnowledgeBase) => void;
  aggregationsData: ReturnType<typeof useAggregations>['data'];
  readOnly?: boolean;
}

const ALL_HUB_SOURCES = ['library', 'organisation'];

const KnowledgeBasePanels: React.FC<KnowledgeBasePanelsProps> = ({
  knowledgeBase,
  onChange,
  aggregationsData,
  readOnly = false
}) => {
  // kbMode is derived directly from knowledgeBase.type — no separate state
  const kbMode = knowledgeBase.type;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFilters, setPickerFilters] = useState<DocumentPickerFilters>(EMPTY_DOCUMENT_PICKER_FILTERS);
  const [pickerSelectedIds, setPickerSelectedIds] = useState<string[]>([]);
  const [pendingMode, setPendingMode] = useState<KnowledgeBase['type'] | null>(null);
  const { data: categoryDefaults } = useCategoryDefaults();
  const { data: groupsList } = useGroups();
  const defaultHubSources = [...ALL_HUB_SOURCES, ...(groupsList?.groups ?? []).map((g) => groupSourceKey(g.id))];

  const picker = useDocumentPicker(pickerFilters);

  // Reactively resolve document IDs → titles for display.
  // This covers newly-added documents that may not yet have titles in the draft.
  const docIds = useMemo(() => (knowledgeBase.documents ?? []).map((d) => d.id), [knowledgeBase.documents]);
  const freshTitles = usePickerTitles(docIds);
  const docTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of knowledgeBase.documents ?? []) map[d.id] = d.title;
    for (const d of freshTitles) map[d.id] = d.title; // fresh titles override
    return map;
  }, [knowledgeBase.documents, freshTitles]);

  const applyModeChange = (next: KnowledgeBase['type']) => {
    if (next === 'default') {
      onChange({ type: 'default' });
    } else if (next === 'specific_categories') {
      // Pre-populate defaults when switching in with no existing selections
      const hasExisting =
        (knowledgeBase.categories?.length ?? 0) > 0 || (knowledgeBase.doc_hub_sources?.length ?? 0) > 0;
      if (hasExisting) {
        onChange({ ...knowledgeBase, type: 'specific_categories', documents: undefined });
      } else {
        const defaults = categoryDefaults
          ? Object.entries(categoryDefaults)
              .filter(([, enabled]) => enabled)
              .map(([name]) => name)
          : [];
        onChange({
          type: 'specific_categories',
          categories: defaults,
          doc_hub_sources: defaultHubSources
        });
      }
    } else {
      onChange({ type: 'specific_documents', documents: [] });
    }
  };

  const handleModeChange = (next: KnowledgeBase['type']) => {
    if (next === kbMode) return;
    const willLoseData =
      (next === 'specific_categories' && (knowledgeBase.documents?.length ?? 0) > 0) ||
      (next === 'specific_documents' && ((knowledgeBase.categories?.length ?? 0) > 0 || knowledgeBase.web_access)) ||
      (next === 'default' &&
        ((knowledgeBase.categories?.length ?? 0) > 0 ||
          (knowledgeBase.documents?.length ?? 0) > 0 ||
          knowledgeBase.web_access));
    if (willLoseData) {
      setPendingMode(next);
    } else {
      applyModeChange(next);
    }
  };

  const modeDescriptions: Record<KnowledgeBase['type'], string> = {
    default:
      'The specialist can access all of the regulatory wiki and your document hub, like in a normal chat session.',
    specific_categories: 'Restrict access to specific sources or categories.',
    specific_documents: 'The specialist can only access the specific documents you select.'
  };

  // Build a ChatSearchFilters-shaped object for SourcesPanel (which still uses the old shape)
  const sourcesFilters = useMemo(
    () => ({
      category: knowledgeBase.categories ?? [],
      domain: [],
      subDomain: [],
      document: [],
      docHubSource: knowledgeBase.doc_hub_sources ?? []
    }),
    [knowledgeBase.categories, knowledgeBase.doc_hub_sources]
  );

  return (
    <div className="rounded-lg bg-white overflow-hidden flex flex-col gap-2">
      <SegmentedControl
        options={[
          { value: 'default', label: 'Default' },
          { value: 'specific_categories', label: 'Specific categories' },
          { value: 'specific_documents', label: 'Specific documents' }
        ]}
        value={kbMode}
        onChange={handleModeChange}
        ariaLabel="Knowledge base mode"
        className="flex w-full"
        disabled={readOnly}
      />
      <span className="flex items-center gap-1 text-content-subtle">
        <InfoIcon size={16} weight="regular" />
        <p className="text-sm">{modeDescriptions[kbMode]}</p>
      </span>

      {kbMode === 'specific_categories' && (
        <div className="border-border-default">
          <SourcesPanel
            chatFilters={sourcesFilters}
            onCategoryChange={readOnly ? () => {} : (cats) => onChange({ ...knowledgeBase, categories: cats })}
            docHubSources={knowledgeBase.doc_hub_sources ?? []}
            onDocHubSourcesChange={
              readOnly ? () => {} : (sources) => onChange({ ...knowledgeBase, doc_hub_sources: sources })
            }
            aggregations={aggregationsData}
            showWebAccess={true}
            webAccessEnabled={Boolean(knowledgeBase.web_access)}
            onToggleWebAccess={
              readOnly ? () => {} : () => onChange({ ...knowledgeBase, web_access: !knowledgeBase.web_access })
            }
            readOnly={readOnly}
          />
        </div>
      )}

      {kbMode === 'specific_documents' && (
        <div className="space-y-2">
          <div className="flex items-end justify-between gap-3 h-[36px]">
            <span className="text-sm text-content-subtler">
              {(knowledgeBase.documents ?? []).length === 0
                ? 'No documents selected yet'
                : `${knowledgeBase.documents!.length} document${knowledgeBase.documents!.length === 1 ? '' : 's'} selected:`}
            </span>
            {!readOnly && (
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  setPickerSelectedIds((knowledgeBase.documents ?? []).map((d) => d.id));
                  setPickerFilters(EMPTY_DOCUMENT_PICKER_FILTERS);
                  setPickerOpen(true);
                }}
              >
                {(knowledgeBase.documents ?? []).length === 0 ? 'Select documents' : 'Edit selection'}
              </Button>
            )}
          </div>
          {(knowledgeBase.documents ?? []).length > 0 && (
            <div className="flex flex-col gap-1">
              {knowledgeBase.documents!.map((doc) => {
                const title = docTitleMap[doc.id] ?? doc.title ?? doc.id;
                return (
                  <div key={doc.id} className="flex items-center justify-between gap-2 h-[26px]">
                    <ResourceLink
                      content={title}
                      data={{
                        id: doc.id,
                        resource_id: doc.id,
                        resource_type: 'document',
                        title,
                        is_wiki: doc.is_wiki ?? false
                      }}
                    />
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={TrashIcon}
                        onClick={() =>
                          onChange({
                            ...knowledgeBase,
                            documents: knowledgeBase.documents!.filter((d) => d.id !== doc.id)
                          })
                        }
                        aria-label={`Remove ${title}`}
                        className="flex-shrink-0"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={<span className="text-lg font-semibold">Pin documents</span>}
        className="w-full max-w-4xl h-[80vh] max-h-[80vh]"
        disableBackdropClick
      >
        <div className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 overflow-hidden">
            <DocumentPickerContent
              rows={picker.rows}
              isLoading={picker.isLoading}
              page={picker.page}
              pageCount={picker.pageCount}
              totalCount={picker.totalCount}
              onPageChange={picker.setPage}
              selectedIds={pickerSelectedIds}
              onSelectionChange={setPickerSelectedIds}
              filters={pickerFilters}
              onFiltersChange={setPickerFilters}
              filterDefs={picker.filterDefs}
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-3 flex-shrink-0 border-t border-border-default mt-2">
            <Button variant="outline" size="md" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                const newDocs = pickerSelectedIds.map((id) => ({ id, title: docTitleMap[id] ?? id }));
                onChange({ ...knowledgeBase, type: 'specific_documents', documents: newDocs });
                setPickerOpen(false);
              }}
              disabled={pickerSelectedIds.length === 0}
            >
              {pickerSelectedIds.length > 0
                ? `Save selection (${pickerSelectedIds.length} document${pickerSelectedIds.length !== 1 ? 's' : ''})`
                : 'Save selection'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={pendingMode !== null}
        onClose={() => setPendingMode(null)}
        onConfirm={() => {
          if (pendingMode) applyModeChange(pendingMode);
          setPendingMode(null);
        }}
        title="Switch knowledge base mode?"
        message={
          pendingMode === 'default'
            ? 'Switching to Default will remove all knowledge base restrictions. This cannot be undone.'
            : pendingMode === 'specific_categories'
              ? 'Switching to Specific categories will clear your pinned documents.'
              : 'Switching to Specific documents will clear your source filters.'
        }
        confirmText="Switch"
        variant="danger"
      />
    </div>
  );
};

// ============================================================
// SpecialistsModal
// ============================================================

interface SpecialistsModalProps {
  open: boolean;
  onClose: () => void;
  /** When supplied, the modal auto-navigates to the detail view for
   * this specialist on open. Used by the ``/specialists/:id`` redirect
   * route so a resource-link click lands straight on the right card. */
  initialSpecialistId?: string | null;
}

const SpecialistsModal: React.FC<SpecialistsModalProps> = ({ open, onClose, initialSpecialistId }) => {
  const {
    selectedSpecialist,
    setSelectedSpecialist,
    specialists,
    specialistsLoading: loading,
    fetchSpecialists,
    addSpecialist,
    updateSpecialist,
    deleteSpecialist
  } = useSpecialist();

  const posthog = usePostHog();
  const { user } = useAuth();

  // ---- Draft-copy pattern ----
  // For "create": draft holds BLANK_SPECIALIST_DRAFT fields, original is null.
  // For "edit": draft is structuredClone(viewingSpecialist), original is the saved specialist.
  const [draft, setDraft] = useState<Partial<Specialist>>(structuredClone(BLANK_SPECIALIST_DRAFT));
  const [original, setOriginal] = useState<Specialist | null>(null);

  // Unfiltered aggregations — SourcesPanel renders ALL category pills so the
  // user can toggle ones whose count would drop to 0 with their own filters.
  const { data: aggregationsData } = useAggregations({ enabled: open });

  // Refresh specialists data when modal opens to reflect any external changes (e.g. LLM changes)
  useEffect(() => {
    if (open) {
      fetchSpecialists();
    }
  }, [open, fetchSpecialists]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out deleted specialists and group/sort them
  const nonDeletedSpecialists = useMemo(() => specialists.filter((s) => !s.deleted_at), [specialists]);

  const filteredSpecialists = useMemo(() => {
    if (!searchQuery.trim()) return nonDeletedSpecialists;
    const query = searchQuery.toLowerCase().trim();
    return nonDeletedSpecialists.filter((s) => {
      const nameMatch = s.name.toLowerCase().includes(query);
      const instructionMatch = s.prompt_parts.instruction?.toLowerCase().includes(query);
      const styleMatch = s.prompt_parts.response_style?.toLowerCase().includes(query);
      return nameMatch || instructionMatch || styleMatch;
    });
  }, [nonDeletedSpecialists, searchQuery]);

  const groupedSpecialists = useMemo(() => {
    const recentlyUsedIds = new Set(
      filteredSpecialists
        .filter((s) => s.last_used_at)
        .sort((a, b) => new Date(b.last_used_at!).getTime() - new Date(a.last_used_at!).getTime())
        .slice(0, 3)
        .map((s) => s.id)
    );

    const recentlyUsed = filteredSpecialists
      .filter((s) => recentlyUsedIds.has(s.id))
      .sort((a, b) => new Date(b.last_used_at!).getTime() - new Date(a.last_used_at!).getTime());
    const createdByYou = filteredSpecialists.filter((s) => !recentlyUsedIds.has(s.id) && s.is_owner);
    const sharedWithYou = filteredSpecialists.filter((s) => !recentlyUsedIds.has(s.id) && !s.is_owner);

    return { recentlyUsed, createdByYou, sharedWithYou };
  }, [filteredSpecialists]);

  const [isEditing, setIsEditing] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [viewingSpecialist, setViewingSpecialist] = useState<Specialist | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [closeWarningOpen, setCloseWarningOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    name?: boolean;
    instruction?: boolean;
  }>({});

  const getCreatorLabel = useCallback((creator?: SpecialistCreator | null) => {
    if (!creator) return 'Deleted User';
    const name = [creator.firstname, creator.surname].filter(Boolean).join(' ').trim() || 'Unknown';
    const organisation = creator.organisation?.trim();
    return organisation ? `${name} at ${organisation}` : name;
  }, []);

  const viewingRole = useMemo(() => {
    if (!viewingSpecialist) return ResourcePermissionRole.READ;
    if (viewingSpecialist.current_user_role !== undefined && viewingSpecialist.current_user_role !== null) {
      return viewingSpecialist.current_user_role;
    }
    return viewingSpecialist.is_owner ? ResourcePermissionRole.OWNER : ResourcePermissionRole.READ;
  }, [viewingSpecialist]);
  const canEditViewing = viewingRole >= ResourcePermissionRole.WRITE;
  const canShareViewing = viewingRole >= ResourcePermissionRole.SHARE;
  const canDeleteViewing = viewingRole >= ResourcePermissionRole.OWNER;

  const hasFetchedRef = useRef(false);
  const formScrollRef = useRef<HTMLDivElement>(null);
  const appliedInitialIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      hasFetchedRef.current = false;
      appliedInitialIdRef.current = null;
      setShowDetailView(false);
      setIsAddingNew(false);
      setIsEditing(false);
      setViewingSpecialist(null);
      return;
    }
    if (open && specialists.length === 0 && !loading && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSpecialists();
    }
  }, [open, specialists.length, loading, fetchSpecialists]);

  useEffect(() => {
    if (
      !open ||
      !initialSpecialistId ||
      loading ||
      specialists.length === 0 ||
      appliedInitialIdRef.current === initialSpecialistId
    ) {
      return;
    }
    const match = specialists.find((s) => s.id === initialSpecialistId);
    if (match) {
      appliedInitialIdRef.current = initialSpecialistId;
      setViewingSpecialist(match);
      setShowDetailView(true);
    }
  }, [open, initialSpecialistId, loading, specialists]);

  // True when there are unsaved changes in the form.
  const isDirty = useMemo(() => {
    if (isAddingNew) {
      return (draft.name?.trim() ?? '') !== '' || (draft.prompt_parts?.instruction?.trim() ?? '') !== '';
    }
    if (isEditing && original) {
      // Compare document IDs only (not titles) to avoid false positives from title enrichment
      const normalise = (s: Partial<Specialist>) => {
        const kb = s.knowledge_base;
        return {
          ...s,
          knowledge_base: kb
            ? {
                ...kb,
                documents: (kb.documents ?? []).map((d) => d.id).sort()
              }
            : undefined
        };
      };
      return JSON.stringify(normalise(draft)) !== JSON.stringify(normalise(original));
    }
    return false;
  }, [isAddingNew, isEditing, draft, original]);

  const handleClose = () => {
    if (isDirty) {
      setCloseWarningOpen(true);
    } else {
      onClose();
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};
    if (!draft.name || draft.name.trim() === '') errors.name = true;
    if (!draft.prompt_parts?.instruction || draft.prompt_parts.instruction.trim() === '') errors.instruction = true;
    setValidationErrors(errors);

    if (draft.knowledge_base?.type === 'specific_documents' && (draft.knowledge_base.documents ?? []).length === 0) {
      setFormError('Please select at least one document to pin, or switch to a different knowledge base mode.');
      return false;
    }

    return Object.keys(errors).length === 0;
  };

  const handleAddSpecialist = async () => {
    if (!validateForm()) return;
    try {
      setFormError(null);
      const newSpecialist = await addSpecialist({
        name: draft.name || 'New Specialist',
        colour: draft.colour || '#8b5cf6',
        prompt_parts: draft.prompt_parts!,
        knowledge_base: draft.knowledge_base ?? { type: 'default' },
        tool_set: draft.tool_set || []
      });
      if (newSpecialist) {
        posthog.capture('chat_specialist_created', { specialist_id: newSpecialist.id });
        setSelectedSpecialist(newSpecialist);
        setIsAddingNew(false);
        resetForm();
        setValidationErrors({});
        onClose();
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to add specialist');
    }
  };

  const handleUpdateSpecialist = async () => {
    if (!viewingSpecialist) return;
    if (!validateForm()) return;
    try {
      setFormError(null);
      const updatedSpecialist = await updateSpecialist(viewingSpecialist.id, {
        name: draft.name,
        colour: draft.colour,
        prompt_parts: draft.prompt_parts,
        knowledge_base: draft.knowledge_base,
        tool_set: draft.tool_set
      });
      if (updatedSpecialist) {
        posthog.capture('chat_specialist_updated', { specialist_id: viewingSpecialist.id });
        setViewingSpecialist(updatedSpecialist);
        setIsEditing(false);
        setOriginal(updatedSpecialist);
        setDraft(structuredClone(updatedSpecialist));
        setValidationErrors({});
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to update specialist');
    }
  };

  const handleDeleteSpecialist = async () => {
    if (!viewingSpecialist) return;
    setDeleteLoading(true);
    const success = await deleteSpecialist(viewingSpecialist.id);
    setDeleteLoading(false);
    if (success) {
      posthog.capture('chat_specialist_deleted', { specialist_id: viewingSpecialist.id });
      if (selectedSpecialist?.id === viewingSpecialist.id) setSelectedSpecialist(null);
      setViewingSpecialist(null);
      setShowDetailView(false);
      setIsEditing(false);
      setDeleteModalOpen(false);
    }
  };

  const startEditing = () => {
    if (!viewingSpecialist || !canEditViewing) return;
    setOriginal(viewingSpecialist);
    setDraft(structuredClone(viewingSpecialist));
    setIsEditing(true);
    setFormError(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setIsAddingNew(false);
    resetForm();
    setValidationErrors({});
    setFormError(null);
  };

  const backToGrid = () => {
    setShowDetailView(false);
    setIsEditing(false);
    setIsAddingNew(false);
    setViewingSpecialist(null);
    resetForm();
    setValidationErrors({});
    setFormError(null);
  };

  const resetForm = () => {
    setDraft(structuredClone(BLANK_SPECIALIST_DRAFT));
    setOriginal(null);
  };

  const startAddingNew = () => {
    setIsAddingNew(true);
    setIsEditing(false);
    setSelectedSpecialist(null);
    setShowDetailView(true);
    resetForm();
    setFormError(null);
    setTimeout(() => {
      formScrollRef.current?.scrollTo({ top: 0 });
    }, 0);
  };

  const openSpecialistDetail = (specialist: Specialist) => {
    setViewingSpecialist(specialist);
    setIsAddingNew(false);
    setIsEditing(false);
    setShowDetailView(true);
    setFormError(null);
  };

  const handleSelectForChat = () => {
    if (viewingSpecialist) {
      setSelectedSpecialist(viewingSpecialist);
      posthog.capture('chat_specialist_selected', {
        specialist_id: viewingSpecialist.id,
        specialist_name: viewingSpecialist.name
      });
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={<h2 className="text-lg font-semibold text-content-default">Specialists</h2>}
      className="mx-1 h-full md:min-w-[700px] md:w-[840px] md:h-[95vh] max-h-[95vh] md:rounded-lg"
    >
      <>
        {!showDetailView ? (
          /* Grid View */
          <div className="flex flex-col h-full">
            <span className="inline-flex items-start justify-between gap-2">
              <span className="text-sm text-gray-500 mb-4">
                Use a Specialist to create a tailored version of the CompliMind assistant for a specific task or domain.
              </span>
              <Link
                to="/tutorials"
                onClick={onClose}
                className="flex items-center pr-2 gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
              >
                <QuestionIcon size={16} />
                <span>Help & Tutorials</span>
              </Link>
            </span>

            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-100">
              <div className="flex-1 max-w-md">
                <SearchInputField
                  placeholder="Search specialists..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button onClick={startAddingNew} variant="primary" size="md" icon={PlusIcon}>
                Create Specialist
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
              {loading ? (
                <div className="flex items-center justify-center h-48 text-gray-500">Loading specialists...</div>
              ) : (
                <>
                  {groupedSpecialists.recentlyUsed.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        Recently Used
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupedSpecialists.recentlyUsed.map((s) => (
                          <SpecialistCard
                            key={s.id}
                            specialist={s}
                            onClick={() => openSpecialistDetail(s)}
                            getCreatorLabel={getCreatorLabel}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                  {groupedSpecialists.createdByYou.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        Created by You ({groupedSpecialists.createdByYou.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupedSpecialists.createdByYou.map((s) => (
                          <SpecialistCard
                            key={s.id}
                            specialist={s}
                            onClick={() => openSpecialistDetail(s)}
                            getCreatorLabel={getCreatorLabel}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                  {groupedSpecialists.sharedWithYou.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        Shared with You ({groupedSpecialists.sharedWithYou.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupedSpecialists.sharedWithYou.map((s) => (
                          <SpecialistCard
                            key={s.id}
                            specialist={s}
                            onClick={() => openSpecialistDetail(s)}
                            getCreatorLabel={getCreatorLabel}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                  {nonDeletedSpecialists.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <p className="text-gray-500 mb-4">No specialists yet</p>
                      <Button onClick={startAddingNew} variant="primary" size="md" icon={PlusIcon}>
                        Create your first Specialist
                      </Button>
                    </div>
                  )}
                  {nonDeletedSpecialists.length > 0 && filteredSpecialists.length === 0 && searchQuery && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <p className="text-gray-500 mb-2">No specialists found for "{searchQuery}"</p>
                      <button onClick={() => setSearchQuery('')} className="text-sm text-blue-600 hover:text-blue-700">
                        Clear search
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : isAddingNew || viewingSpecialist ? (
          (() => {
            const isCreate = isAddingNew;
            const isView = !isCreate && !isEditing;
            const disabled = isView;

            // In view mode, read directly from viewingSpecialist; otherwise read from draft.
            const name = isView ? viewingSpecialist!.name : draft.name;
            const colour = isView ? viewingSpecialist!.colour : (draft.colour ?? '#8b5cf6');
            const instruction = isView
              ? viewingSpecialist!.prompt_parts.instruction
              : (draft.prompt_parts?.instruction ?? '');
            const responseStyle = isView
              ? viewingSpecialist!.prompt_parts.response_style
              : draft.prompt_parts?.response_style;

            // The knowledge base shown in the panels
            const panelKb: KnowledgeBase = isView
              ? viewingSpecialist!.knowledge_base
              : (draft.knowledge_base ?? { type: 'default' });

            return (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b">
                  <div className="flex items-center gap-3">
                    <button onClick={backToGrid} className="p-1 hover:bg-gray-100 rounded">
                      <ArrowLeftIcon size={20} />
                    </button>
                    {!isCreate && (
                      <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: colour }} />
                    )}
                    <h3 className="font-semibold text-lg">{isCreate ? 'New Specialist' : name}</h3>
                  </div>
                  {isView && (
                    <div className="flex gap-2">
                      {canShareViewing && (
                        <Button onClick={() => setShareModalOpen(true)} icon={ShareIcon} variant="outline" size="md">
                          Share
                        </Button>
                      )}
                      {canEditViewing && (
                        <Button onClick={startEditing} icon={PencilSimpleIcon} variant="outline" size="md">
                          Edit
                        </Button>
                      )}
                      {canDeleteViewing && (
                        <Button onClick={() => setDeleteModalOpen(true)} icon={TrashIcon} variant="outline" size="md">
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                  {!isView && !isCreate && (
                    <div className="flex gap-2">
                      <Button onClick={cancelEditing} variant="outline" size="md">
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateSpecialist} variant="primary" size="md" icon={CheckIcon}>
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                {/* Attribution */}
                {viewingSpecialist?.shared_by && (
                  <div className="text-xs text-gray-500 mb-4">
                    Shared by {getCreatorLabel(viewingSpecialist.shared_by)}
                  </div>
                )}
                {viewingSpecialist &&
                  !viewingSpecialist.shared_by &&
                  !viewingSpecialist.is_owner &&
                  viewingSpecialist.creator && (
                    <div className="text-xs text-gray-500 mb-4">
                      Created by {getCreatorLabel(viewingSpecialist.creator)}
                    </div>
                  )}

                {formError && (
                  <div className="text-sm text-misc-error bg-red-50 border border-red-200 rounded px-3 py-2 mb-4">
                    {formError}
                  </div>
                )}

                {/* Form body */}
                <div ref={formScrollRef} className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-2 pb-4">
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Name {!isView && <span className="text-misc-error">*</span>}
                      </label>
                      <Input
                        value={name}
                        onChange={(e) => {
                          setDraft((prev) => ({ ...prev, name: e.target.value }));
                          if (validationErrors.name && e.target.value.trim() !== '') {
                            setValidationErrors((v) => ({ ...v, name: false }));
                          }
                        }}
                        placeholder="Enter name for your specialist, e.g. 'Research Assistant'"
                        disabled={disabled}
                        state={!disabled && validationErrors.name ? 'error' : 'default'}
                        errorMessage={!disabled && validationErrors.name ? 'Name is required' : undefined}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Colour</label>
                      <ColorPicker
                        selectedColor={colour}
                        onColorSelect={(c) => setDraft((prev) => ({ ...prev, colour: c }))}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Instruction (who the specialist is and what they will do){' '}
                      {!isView && <span className="text-misc-error">*</span>}
                    </label>
                    <Textarea
                      value={instruction}
                      onChange={(e) => {
                        setDraft((prev) => ({
                          ...prev,
                          prompt_parts: { ...prev.prompt_parts!, instruction: e.target.value }
                        }));
                        if (validationErrors.instruction && e.target.value.trim() !== '') {
                          setValidationErrors((v) => ({ ...v, instruction: false }));
                        }
                      }}
                      placeholder="e.g. You are an expert in wastewater management who reviews my information solely against HTM 07-01."
                      rows={3}
                      disabled={disabled}
                      state={!disabled && validationErrors.instruction ? 'error' : 'default'}
                      errorMessage={!disabled && validationErrors.instruction ? 'Instruction is required' : undefined}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Response Style (how the specialist will respond to you)
                    </label>
                    <Textarea
                      value={responseStyle}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          prompt_parts: { ...prev.prompt_parts!, response_style: e.target.value }
                        }))
                      }
                      placeholder="e.g. Speak very concisely, and always cite HTM 0701 to back up your answers."
                      rows={2}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Knowledge Base (what documents the specialist can access)
                    </label>
                    <KnowledgeBasePanels
                      key={
                        isCreate
                          ? 'create'
                          : isEditing
                            ? `edit-${viewingSpecialist?.id}`
                            : `view-${viewingSpecialist?.id}`
                      }
                      knowledgeBase={panelKb}
                      onChange={(kb) => {
                        setDraft((prev) => ({ ...prev, knowledge_base: kb }));
                        // Clear "no docs pinned" error when documents are added
                        if (kb.type === 'specific_documents' && (kb.documents ?? []).length > 0) {
                          setFormError(null);
                        }
                      }}
                      aggregationsData={aggregationsData}
                      readOnly={isView}
                    />
                  </div>
                </div>

                {/* Footer */}
                {isCreate && (
                  <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
                    <Button onClick={backToGrid} variant="outline" size="md">
                      Cancel
                    </Button>
                    <Button onClick={handleAddSpecialist} variant="primary" size="md" icon={CheckIcon}>
                      Save
                    </Button>
                  </div>
                )}
                {isView && (
                  <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
                    <Button onClick={handleSelectForChat} variant="primary" size="md" icon={ChatCircleIcon}>
                      Select in Chat
                    </Button>
                  </div>
                )}
              </div>
            );
          })()
        ) : null}
      </>

      {viewingSpecialist && (
        <ConfirmModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteSpecialist}
          title="Delete Specialist"
          message={`Are you sure you want to delete "${viewingSpecialist.name}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          isLoading={deleteLoading}
        />
      )}

      {viewingSpecialist && canShareViewing && (
        <SpecialistShareModal
          specialistId={viewingSpecialist.id}
          specialistName={viewingSpecialist.name}
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          userEmail={user?.email}
          organisationName={user?.organisation || undefined}
          isOwner={viewingSpecialist.is_owner}
          userPermissionLevel={viewingRole}
        />
      )}

      <ConfirmModal
        open={closeWarningOpen}
        onClose={() => setCloseWarningOpen(false)}
        onConfirm={() => {
          setCloseWarningOpen(false);
          onClose();
        }}
        title="Discard changes?"
        message="You have unsaved changes. If you close now they will be lost."
        confirmText="Discard"
        variant="danger"
      />
    </Modal>
  );
};

export default SpecialistsModal;
